import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavGroup, NavItem } from "./types";
import { useState } from "react";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { cn } from "~/lib/utils";
import { Link } from "../Link";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "../Badge";
import { areNavItemsActive, isNavItemActive } from "./isNavItemActive";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { Anchor } from "../Anchor";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";

// // // //

export function SidebarNavItem(props: {
    item: NavItem;
    nested?: boolean;
    activePathname: string;
    collapsed: boolean;
    parentGroup?: NavGroup;
}) {
    const {
        item,
        nested = false,
        activePathname,
        collapsed,
        parentGroup,
    } = props;
    const { items = [], defaultExpandedState = false } = item;

    const isActive = isNavItemActive({ item, pathname: activePathname });
    const hasActiveChild = areNavItemsActive({
        items,
        pathname: activePathname,
    });
    const isActiveLeaf = isActive && !hasActiveChild;

    const [expanded, setExpanded] = useState(
        hasActiveChild || defaultExpandedState,
    );

    const collapsedNavItemClassName = cn(
        "rounded-sm p-2 my-1 flex transition-colors",
        {
            "bg-muted text-link": isActiveLeaf,
            "text-link": hasActiveChild,
            "hover:bg-muted text-sidebar-foreground hover:text-link":
                !isActiveLeaf && !hasActiveChild,
        },
    );

    const expandedNavItemClassName = cn(
        "flex items-center justify-between rounded-sm px-3 transition-colors",
        {
            "bg-muted text-link font-medium": isActiveLeaf,
            "text-link": hasActiveChild,
            "text-sidebar-foreground hover:bg-muted hover:text-link":
                !isActiveLeaf && !hasActiveChild,
        },
    );

    const collapseButtonIconClassName = cn("h-[16px] w-[16px]", {
        "text-link": isActiveLeaf || hasActiveChild,
        "text-sidebar-foreground hover:text-link":
            !isActiveLeaf && !hasActiveChild,
    });

    // Render "collapsed" variation
    if (collapsed) {
        return (
            <FeatureFlag feature={item.featureFlag}>
                <Tooltip
                    placement="right"
                    tooltipContent={
                        <div className="p-2 font-light w-auto max-w-160">
                            {parentGroup && parentGroup.label && (
                                <p className="text-sm mb-1 underline font-thin underline-offset-4">
                                    {parentGroup.label}
                                </p>
                            )}
                            <p className="">{item.label}</p>
                        </div>
                    }
                >
                    {({ setTriggerRef }) => (
                        <>
                            {item.href && (
                                <Link
                                    setTriggerRef={setTriggerRef}
                                    href={item.href}
                                    className={collapsedNavItemClassName}
                                    analyticsLabel={`Sidebar Item Click - ${item.label} (Collapsed)`}
                                >
                                    {item.icon && (
                                        <Icon
                                            className={cn("w-4 h-4")}
                                            icon={item.icon}
                                        />
                                    )}
                                </Link>
                            )}

                            {!item.href && (
                                <div
                                    ref={setTriggerRef}
                                    className={collapsedNavItemClassName}
                                >
                                    {item.icon && (
                                        <Icon
                                            className={cn("w-4 h-4")}
                                            icon={item.icon}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </Tooltip>
            </FeatureFlag>
        );
    }

    // Define collapse button
    const collapseButton = (
        <>
            {items.length > 0 && (
                <button
                    className="ml-2 p-2"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setExpanded(!expanded);
                    }}
                >
                    <FontAwesomeIcon
                        icon={expanded ? faChevronDown : faChevronLeft}
                        className={collapseButtonIconClassName}
                    />
                </button>
            )}
        </>
    );

    // Define nested items
    const nestedItems = (
        <>
            {expanded && !collapsed && (
                <>
                    {items.map((n) => {
                        return (
                            <SidebarNavItem
                                key={n.key}
                                item={n}
                                nested={true}
                                activePathname={activePathname}
                                collapsed={collapsed}
                            />
                        );
                    })}
                </>
            )}
        </>
    );

    // Define content rendered inside Anchor / Link
    const linkContent = (
        <span
            className={cn("flex items-center w-full", {
                "py-2": !collapsed,
                "pl-5": nested,
            })}
        >
            {item.icon && (
                <span className="relative flex">
                    <Icon
                        className={cn("w-4 h-4", {
                            "mr-2": !collapsed,
                            [item.iconClassName ?? ""]: !isActive,
                        })}
                        icon={item.icon}
                    />
                    {item.badge && collapsed && (
                        <span
                            className={cn(
                                "absolute -right-1.5 -top-1.5 block h-2 w-2 rounded-full",
                                {
                                    "bg-slate-600":
                                        item.badge.variant === "gray",
                                    "bg-red-600": item.badge.variant === "red",
                                    "bg-yellow-600":
                                        item.badge.variant === "yellow",
                                    "bg-green-600":
                                        item.badge.variant === "green",
                                    "bg-primary-dark":
                                        item.badge.variant === "blue",
                                    "bg-indigo-600":
                                        item.badge.variant === "indigo",
                                    "bg-purple-600":
                                        item.badge.variant === "purple",
                                    "bg-pink-600":
                                        item.badge.variant === "pink",
                                },
                            )}
                        />
                    )}
                </span>
            )}
            {!collapsed && (
                <span className="text-sm whitespace-nowrap">{item.label}</span>
            )}
            {!collapsed && item.badge && (
                <Badge
                    label={item.badge.text}
                    variant={item.badge.variant}
                    rounded="md"
                    className="ml-2 whitespace-nowrap"
                    size="xs"
                />
            )}
        </span>
    );

    // Render placeholder if item.href is undefined
    if (item.href === undefined) {
        return (
            <FeatureFlag
                feature={item.featureFlag}
                invert={item.invertFeatureFlagBehavior}
            >
                <div
                    className={cn(
                        "flex items-center justify-between rounded-sm px-3",
                        {
                            "text-link": hasActiveChild,
                            "text-sidebar-foreground": !hasActiveChild,
                        },
                    )}
                >
                    {linkContent}
                    {collapseButton}
                </div>

                {nestedItems}
            </FeatureFlag>
        );
    }

    // Render <Anchor> if external
    if (item.external) {
        return (
            <FeatureFlag
                feature={item.featureFlag}
                invert={item.invertFeatureFlagBehavior}
            >
                <Anchor
                    href={item.href}
                    openInNewTab
                    className={expandedNavItemClassName}
                    analyticsLabel={`Sidebar Item Click - ${item.label}`}
                >
                    {linkContent}
                </Anchor>
            </FeatureFlag>
        );
    }

    // Render <Link>
    return (
        <FeatureFlag
            feature={item.featureFlag}
            invert={item.invertFeatureFlagBehavior}
        >
            <Link
                href={item.href}
                className={expandedNavItemClassName}
                analyticsLabel={`Sidebar Item Click - ${item.label}`}
            >
                {linkContent}
                {collapseButton}
            </Link>

            {nestedItems}
        </FeatureFlag>
    );
}
